import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiGet } from "../../lib/api";
import { cn } from "../../lib/cn";

type Client = {
  id: string;
  full_name: string;
  phone?: string | null;
  email?: string | null;
  risk_tier: string;
  risk_score: number;
  visits_total: number;
  no_show_total: number;
  telegram_chat_id?: string | null;
};

type ClientsData = { items: Client[]; meta: { total: number } };

const RISK_COLORS: Record<string, string> = {
  minimal: "text-green-600",
  low: "text-green-500",
  medium: "text-yellow-600",
  high: "text-red-500",
  critical: "text-red-700 font-bold",
};

function useDebounce<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const h = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(h);
  }, [value, delay]);
  return debounced;
}

export function TgClientsPage() {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [expanded, setExpanded] = useState<string | null>(null);
  const debouncedQuery = useDebounce(query, 300);

  const { data, isLoading } = useQuery<ClientsData>({
    queryKey: ["tg-clients", debouncedQuery],
    queryFn: () => {
      const params = new URLSearchParams({ size: "20" });
      if (debouncedQuery) params.set("q", debouncedQuery);
      return apiGet(`/v3/tg/clients?${params}`);
    },
  });

  return (
    <div className="p-4 space-y-4">
      <h1 className="text-lg font-bold text-gray-900">Clients</h1>

      <input
        type="search"
        placeholder="Search by name, phone, email…"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        className="w-full border border-gray-300 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
      />

      {isLoading && <div className="text-gray-400 text-center py-6">Searching…</div>}

      {!isLoading && (data?.items ?? []).length === 0 && query && (
        <div className="text-gray-400 text-center py-8">No clients found for "{query}".</div>
      )}

      {!isLoading && (data?.items ?? []).length === 0 && !query && (
        <div className="text-gray-400 text-center py-8">No clients yet.</div>
      )}

      <div className="space-y-2">
        {(data?.items ?? []).map((client) => (
          <div key={client.id} className="bg-white rounded-xl border border-gray-100 shadow-sm">
            <button
              className="w-full p-4 flex items-center justify-between text-left"
              onClick={() => setExpanded(expanded === client.id ? null : client.id)}
            >
              <div>
                <p className="font-semibold text-gray-900">{client.full_name}</p>
                <p className="text-sm text-gray-500">{client.phone ?? client.email ?? "No contact"}</p>
              </div>
              <span className={cn("text-xs font-medium", RISK_COLORS[client.risk_tier] ?? "text-gray-500")}>
                {client.risk_tier}
              </span>
            </button>

            {expanded === client.id && (
              <div className="border-t border-gray-100 px-4 py-3 space-y-1.5 text-sm">
                <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                  <span className="text-gray-500">Risk score</span>
                  <span className={RISK_COLORS[client.risk_tier] ?? "text-gray-700"}>{client.risk_score}</span>
                  <span className="text-gray-500">Visits</span>
                  <span>{client.visits_total}</span>
                  <span className="text-gray-500">No-shows</span>
                  <span className={client.no_show_total > 0 ? "text-red-600" : ""}>{client.no_show_total}</span>
                  {client.telegram_chat_id && (
                    <>
                      <span className="text-gray-500">Telegram</span>
                      <span className="text-green-600 text-xs">linked</span>
                    </>
                  )}
                </div>
                {client.phone && (
                  <a href={`tel:${client.phone}`} className="block text-teal-600 underline">
                    Call {client.phone}
                  </a>
                )}
                <button
                  onClick={() => {
                    const params = new URLSearchParams({ customer_id: client.id, customer_name: client.full_name });
                    navigate(`/tg/book?${params.toString()}`);
                  }}
                  className="w-full mt-2 bg-teal-600 text-white py-2 rounded-lg text-sm font-semibold"
                >
                  Assign Procedure
                </button>
                <button
                  onClick={() => {
                    const params = new URLSearchParams({
                      customer_id: client.id,
                      customer_name: client.full_name,
                      repeat_last: "1",
                    });
                    navigate(`/tg/book?${params.toString()}`);
                  }}
                  className="w-full mt-2 border border-teal-200 text-teal-700 bg-teal-50 py-2 rounded-lg text-sm font-semibold"
                >
                  Repeat Last Procedure
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
